/**
 * Push Notification Token Registration
 *
 * Handles the lifecycle of push notification tokens in the native app:
 * 1. On page load (when authenticated in native app): requests token from native, registers with backend
 * 2. On token refresh (native pushes new token): re-registers with backend
 *
 * This module relies on:
 * - window.nativeCalls.getDeviceToken() (defined in react-native-link.js)
 * - CSRF token meta tag: <meta name="csrf-token" content="...">
 * - native:tokenRefresh event (dispatched by nativeLink.postEvent in react-native-link.js)
 */

const PUSH_PROVIDER_FCM = 2
const REGISTRATION_COOLDOWN_MS = 15 * 60 * 1000
const STORAGE_KEY_LAST_REGISTRATION = 'push-registration:last-registration'

function getCsrfToken () {
  return document.head.querySelector('meta[name="csrf-token"]')?.content ?? null
}

function getRequestHeaders () {
  const csrfToken = getCsrfToken()

  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
  }
}

function isPushRegistrationDisabled () {
  const value = document.head.querySelector('meta[name="push-registration"]')?.content
  return value === 'disabled'
}

function readLastRegistration () {
  try {
    const lastRegistration = window.localStorage.getItem(STORAGE_KEY_LAST_REGISTRATION)
    if (!lastRegistration) {
      return null
    }

    return JSON.parse(lastRegistration)
  } catch {
    return null
  }
}

function writeLastRegistration (token) {
  try {
    window.localStorage.setItem(STORAGE_KEY_LAST_REGISTRATION, JSON.stringify({
      token: token,
      registeredAt: Date.now(),
    }))
  } catch {
    // Ignore storage errors and continue.
  }
}

function shouldRegisterToken (token, force = false) {
  const lastRegistration = readLastRegistration()
  if (!lastRegistration?.token || !lastRegistration?.registeredAt) {
    return true
  }

  if (lastRegistration.token !== token) {
    return true
  }

  if (force) {
    return true
  }

  return Date.now() - Number(lastRegistration.registeredAt) >= REGISTRATION_COOLDOWN_MS
}

/**
 * Register a push token with the backend.
 */
async function registerToken (token, force = false) {
  if (!token) {
    console.debug('push-registration: no token to register')
    return
  }

  if (!shouldRegisterToken(token, force)) {
    console.debug('push-registration: token registration skipped because of cooldown')
    return
  }

  try {
    const response = await fetch('/device-tokens', {
      method: 'POST',
      credentials: 'same-origin',
      headers: getRequestHeaders(),
      body: JSON.stringify({
        token: token,
        provider: PUSH_PROVIDER_FCM,
      }),
    })

    if (response.status === 401) {
      console.debug('push-registration: skipping token registration for unauthenticated request')
      return
    }

    if (!response.ok) {
      const responseText = await response.text()
      console.error('push-registration: failed to register token', response.status, responseText)
      return
    }

    const responseData = await response.json().catch(() => null)
    writeLastRegistration(token)
    console.debug('push-registration: token registered', responseData)
  } catch (error) {
    console.error('push-registration: failed to register token', error)
  }
}

async function unregisterLastRegisteredToken () {
  const lastRegistration = readLastRegistration()
  const token = lastRegistration?.token

  if (!token) {
    return false
  }

  try {
    const response = await fetch('/device-tokens', {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: getRequestHeaders(),
      body: JSON.stringify({ token: token }),
    })

    if (response.ok) {
      window.localStorage.removeItem(STORAGE_KEY_LAST_REGISTRATION)
      return true
    }
  } catch (error) {
    console.debug('push-registration: failed to unregister token', error)
  }

  return false
}

async function getNotificationPermissionStatus () {
  if (!window.appSecurity?.getNotificationPermissionStatus) {
    return {
      enabled: false,
      status: 'unsupported',
      canAskAgain: false,
    }
  }

  try {
    return await window.appSecurity.getNotificationPermissionStatus()
  } catch (error) {
    console.debug('push-registration: could not get notification permission status', error)

    return {
      enabled: false,
      status: 'unknown',
      canAskAgain: false,
    }
  }
}

async function requestNotificationPermissionIfNeeded (options = {}) {
  const permissionStatus = await getNotificationPermissionStatus()

  if (permissionStatus.enabled) {
    return permissionStatus
  }

  if (!permissionStatus.canAskAgain && options.openSettingsIfDenied !== true) {
    console.debug('push-registration: notifications disabled and cannot ask again without opening settings')
    return permissionStatus
  }

  if (!window.appSecurity?.enableNotifications) {
    return permissionStatus
  }

  try {
    const enableResult = await window.appSecurity.enableNotifications({
      openSettingsIfDenied: options.openSettingsIfDenied === true,
    })

    return {
      ...permissionStatus,
      ...enableResult,
    }
  } catch (error) {
    console.debug('push-registration: could not enable notifications', error)
    return permissionStatus
  }
}

/**
 * Request the device token from the native app and register it.
 */
async function requestAndRegisterToken (options = {}) {
  if (!window.nativeCalls?.getDeviceToken) {
    console.debug('push-registration: getDeviceToken not available')
    return
  }

  try {
    const permissionStatus = options.skipPermissionCheck
      ? await getNotificationPermissionStatus()
      : await requestNotificationPermissionIfNeeded({
        openSettingsIfDenied: options.openSettingsIfDenied === true,
      })

    if (!permissionStatus?.enabled) {
      console.debug('push-registration: notifications are not enabled, skipping token registration', permissionStatus)
      return permissionStatus
    }

    const token = await window.nativeCalls.getDeviceToken()
    await registerToken(token, options.force === true)

    return permissionStatus
  } catch (error) {
    console.debug('push-registration: could not get device token', error)
    return null
  }
}

async function enableNotificationsAndRegisterToken (options = {}) {
  const permissionStatus = await requestNotificationPermissionIfNeeded({
    openSettingsIfDenied: options.openSettingsIfDenied !== false,
  })

  if (!permissionStatus?.enabled) {
    return permissionStatus
  }

  await requestAndRegisterToken({
    skipPermissionCheck: true,
    force: options.force === true,
  })

  return permissionStatus
}

/**
 * Initialize push registration when running in the native app.
 */
function init () {
  if (!window.isNative) {
    return
  }

  if (isPushRegistrationDisabled()) {
    console.debug('push-registration: disabled for this page')
    return
  }

  console.debug('push-registration: initializing')

  /* Register the current token on page load */
  requestAndRegisterToken({
    openSettingsIfDenied: false,
  })

  /* Listen for token refreshes from the native app */
  document.addEventListener('native:tokenRefresh', (event) => {
    console.debug('push-registration: token refreshed', event.detail)
    const token = event.detail?.token
    if (token) {
      registerToken(token, true)
    }
  })
}

/* Run after the DOM is ready (react-native-link.js must be loaded first) */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}

window.pushRegistration = {
  getNotificationPermissionStatus: getNotificationPermissionStatus,
  enableNotificationsAndRegisterToken: enableNotificationsAndRegisterToken,
  requestAndRegisterToken: requestAndRegisterToken,
  unregisterLastRegisteredToken: unregisterLastRegisteredToken,
}
